import { Box, Button, CircularProgress, Grid, Snackbar, TextField, Typography } from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import { useTheme } from "@mui/material/styles";
import EmailIcon from "@mui/icons-material/Email";
import GitHubIcon from "@mui/icons-material/GitHub";
import TelegramIcon from "@mui/icons-material/Telegram";
import { SvgIcon } from "@mui/material";
import { useState } from "react";

const XIcon = () => (
  <SvgIcon>
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </SvgIcon>
);
const DiscordIcon = () => (
  <SvgIcon>
    <path d="M19.27 5.33C17.94 4.71 16.5 4.26 15 4a.09.09 0 0 0-.07.03c-.18.33-.39.76-.53 1.09a16.09 16.09 0 0 0-4.8 0c-.14-.34-.36-.76-.54-1.09c-.01-.02-.04-.03-.07-.03c-1.5.26-2.93.71-4.27 1.33c-.01 0-.02.01-.03.02c-2.72 4.07-3.47 8.03-3.1 11.95c0 .02.01.04.03.05c1.8 1.32 3.53 2.12 5.24 2.65c.03.01.06 0 .07-.02c.4-.55.76-1.13 1.07-1.74c.02-.04 0-.08-.04-.09c-.57-.22-1.11-.48-1.64-.78c-.04-.02-.04-.08-.01-.11c.11-.08.22-.17.33-.25c.02-.02.05-.02.07-.01c3.44 1.57 7.15 1.57 10.55 0c.02-.01.05-.01.07.01c.11.09.22.17.33.26c.04.03.04.09-.01.11c-.52.31-1.07.56-1.64.78c-.04.01-.05.06-.04.09c.32.61.68 1.19 1.07 1.74c.03.01.06.02.09.01c1.72-.53 3.45-1.33 5.24-2.65c.02-.01.03-.03.03-.05c.44-4.53-.73-8.46-3.1-11.95c-.01-.01-.02-.02-.04-.02zM8.52 14.91c-1.03 0-1.89-.95-1.89-2.12s.84-2.12 1.89-2.12c1.06 0 1.9.96 1.89 2.12c0 1.17-.84 2.12-1.89 2.12zm6.97 0c-1.03 0-1.89-.95-1.89-2.12s.84-2.12 1.89-2.12c1.06 0 1.9.96 1.89 2.12c0 1.17-.83 2.12-1.89 2.12z" />
  </SvgIcon>
);

type ContactProps = { isSmallScreen: boolean };

function Contact(_props: ContactProps) {
  const theme = useTheme();
  const [snackOpen, setSnackOpen] = useState(false);
  const [snackMessage, setSnackMessage] = useState("Copied to clipboard!");
  const [form, setForm] = useState({ name: "", email: "", message: "", website: "" });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;

    setSubmitting(true);
    try {
      const res = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (res.ok) {
        setSnackMessage("Message sent! I'll get back to you soon.");
        setForm({ name: "", email: "", message: "", website: "" });
      } else {
        setSnackMessage(data.error || "Failed to send. Try again later.");
      }
    } catch {
      setSnackMessage("Network error. Try again later.");
    } finally {
      setSubmitting(false);
      setSnackOpen(true);
    }
  };

  const links = [
    {
      icon: <EmailIcon />,
      label: "philanthropist0427@gmail.com",
      url: "mailto:philanthropist0427@gmail.com",
    },
    {
      icon: <GitHubIcon />,
      label: "github.com/steller102",
      url: "https://github.com/steller102",
    },
    {
      icon: <XIcon />,
      label: "@steller91102",
      url: "https://x.com/steller91102",
    },
    {
      icon: <TelegramIcon />,
      label: "@steller102",
      url: "https://t.me/steller102",
    },
    {
      icon: <DiscordIcon />,
      label: "steller102",
      url: "",
      copyText: "steller102",
    },
  ];

  return (
    <Box sx={{ py: 6, pb: 10 }}>
      <Typography variant="h1" fontWeight={700}>
        Get in{" "}
        <span style={{ color: theme.palette.secondary.main }}>Touch</span>
      </Typography>
      <Typography variant="body1" sx={{ mb: 3 }}>
        Feel free to reach out — I'm always open to new opportunities and
        collaborations.
      </Typography>
      <Grid container spacing={2}>
        {links.map((link) => (
          <Grid item xs={12} sm={6} md={4} key={link.label}>
            <Box
              component={link.url ? "a" : "div"}
              href={link.url || undefined}
              target={link.url ? "_blank" : undefined}
              rel={link.url ? "noreferrer" : undefined}
              onClick={link.copyText ? () => {
                navigator.clipboard.writeText(link.copyText!);
                setSnackMessage("Copied to clipboard!");
                setSnackOpen(true);
              } : undefined}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                p: 2,
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 1,
                textDecoration: "none",
                color: theme.palette.primary.main,
                cursor: "pointer",
                transition: "border-color 0.2s",
                "&:hover": { borderColor: theme.palette.secondary.main },
              }}
            >
              {link.icon}
              <Typography variant="body1">
                {link.label}
                {link.copyText && <Typography component="span" variant="caption" sx={{ ml: 1, opacity: 0.5 }}>click to copy</Typography>}
              </Typography>
            </Box>
          </Grid>
        ))}
      </Grid>
      <Box sx={{ mt: 6 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
          <Box sx={{ flex: 1, height: "1px", backgroundColor: theme.palette.divider }} />
          <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
            Or send me a message
          </Typography>
          <Box sx={{ flex: 1, height: "1px", backgroundColor: theme.palette.divider }} />
        </Box>
        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{ display: "flex", flexDirection: "column", gap: 2 }}
        >
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label="Name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                inputProps={{ maxLength: 100 }}
                disabled={submitting}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                type="email"
                label="Email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                inputProps={{ maxLength: 200 }}
                disabled={submitting}
              />
            </Grid>
          </Grid>
          <TextField
            fullWidth
            required
            multiline
            minRows={4}
            label="Message"
            value={form.message}
            onChange={(e) => setForm({ ...form, message: e.target.value })}
            inputProps={{ maxLength: 5000 }}
            disabled={submitting}
          />
          <input
            type="text"
            name="website"
            value={form.website}
            onChange={(e) => setForm({ ...form, website: e.target.value })}
            tabIndex={-1}
            autoComplete="off"
            style={{ position: "absolute", left: "-9999px", width: "1px", height: "1px", opacity: 0 }}
            aria-hidden="true"
          />
          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <Button
              type="submit"
              variant="contained"
              disabled={submitting}
              endIcon={submitting ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
              sx={{
                textTransform: "initial",
                backgroundColor: theme.palette.secondary.main,
                "&:hover": { backgroundColor: "#0d8fd4" },
              }}
            >
              {submitting ? "Sending..." : "Send Message"}
            </Button>
          </Box>
        </Box>
      </Box>
      <Box sx={{ mt: 4, textAlign: "center" }}>
        <Typography
          variant="body2"
          sx={{ color: theme.palette.text.secondary }}
        >
          Also check out my{" "}
          <a
            href="https://steller102-3d.vercel.app"
            target="_blank"
            rel="noreferrer"
            style={{ color: theme.palette.secondary.main }}
          >
            interactive 3D portfolio
          </a>
        </Typography>
      </Box>
      <Snackbar
        open={snackOpen}
        autoHideDuration={3000}
        onClose={() => setSnackOpen(false)}
        message={snackMessage}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />
    </Box>
  );
}

export default Contact;
